import { ProviderNetworkType } from "@app/projects/enumerators";
import {
  AttestationProjectResults,
  AttestationRegionSetAggregate,
  AttestationSpecialtyGroupAggregate,
  AttestationSummaryByRegionSet,
} from "@app/projects/results/attestation";
import {
  ProjectionProvidersByProjectionType,
} from "@app/projects/results/projection-provider";
import {
  ServicingProviderComparisonResult,
  ServicingProviderDetail,
  ServicingProviderResult,
} from "@app/projects/results/servicing-provider";
import { ServiceBase } from "@app/shared";

/** Handles communication across the wire when retrieving and interacting with projects. */
class AttestationService extends ServiceBase {
  /**
   * Retrieves the results for a given project and run configuration.
   * @param projectId The identifier of the project.
   * @param runConfigurationId The run configuration associated with the desired results.
   */
  public async detailResults(projectId: number, runConfigurationId: number): Promise<AttestationProjectResults> {
    return null; //TODO: IMPLEMENT ATTESTATION
    // If we don't have a projectId or runConfigurationId, return early.
    if (!projectId || !runConfigurationId) {
      return null;
    }

    return await this.makeGet<AttestationProjectResults>(`attestation/projects/${projectId}/run-configurations/${runConfigurationId}/detail-results`);
  }

  /**
   * Retrieves the results for a given project, region, and run configuration.
   * @param projectId The identifier of the project.
   * @param regionSetId The region set associated with the desired results.
   * @param runConfigurationId The run configuration associated with the desired results.
   */
  public async regionResults(projectId: number, regionSetId: number, runConfigurationId: number): Promise<AttestationRegionSetAggregate> {
    // If we don't have a projectId, regionSetId or runConfigurationId, return early.
    if (!projectId || !regionSetId || !runConfigurationId) {
      return null;
    }

    const res = await this.makeOptionalGet<AttestationRegionSetAggregate>(`attestation/projects/${projectId}/run-configurations/${runConfigurationId}/region-sets/${regionSetId}/summary-results`);

    return res || null;
  }

  /**
   * Retrieves the results for a given project, region, and run configuration.
   * @param projectId The identifier of the project.
   * @param regionSetId The region set associated with the desired results.
   * @param runConfigurationId The run configuration associated with the desired results.
   */
  public async specialtyGroupResults(projectId: number, regionSetId: number, runConfigurationId: number): Promise<AttestationSpecialtyGroupAggregate[]> {
    // If we don't have a projectId, regionSetId or runConfigurationId, return early.
    if (!projectId || !regionSetId || !runConfigurationId) {
      return null;
    }

    return await this.makeGet<AttestationSpecialtyGroupAggregate[]>(`attestation/projects/${projectId}/run-configurations/${runConfigurationId}/region-sets/${regionSetId}/detail-results`);
  }

  /**
   * Retrieves the attestation detail results for a given project, region & run-configuration
   */
  public async regionDetails(projectId: number, regionSetId: number, runConfigurationId: number): Promise<AttestationSummaryByRegionSet[]> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId) {
      return null;
    }

    return await this.makeGet<AttestationSummaryByRegionSet[]>(`attestation/projects/${projectId}/run-configurations/${runConfigurationId}/region-sets/${regionSetId}/summaries`);
  }

  /**
   * Retrieves the attestation detail results for a given project, region, run-configuration & specialty-group-code
   */
  public async regionDetailsByRegionSet(projectId: number, regionSetId: number, runConfigurationId: number, specialtyGroupCode: string): Promise<AttestationSummaryByRegionSet[]> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode) {
      return null;
    }

    return await this.makeGet<AttestationSummaryByRegionSet[]>(`attestation/projects/${projectId}/run-configurations/${runConfigurationId}/region-sets/${regionSetId}/summaries/by-region-set`, { specialtyGroupCode });
  }

  /**
   * Retrieves the attestation detail results for a given project, region, run-configuration & specialty-group-code
   */
  public async regionDetailsBySpecialtyGroup(projectId: number, regionSetId: number, runConfigurationId: number, specialtyGroupCode: string): Promise<AttestationSummaryByRegionSet[]> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode) {
      return null;
    }

    return await this.makeGet<AttestationSummaryByRegionSet[]>(`attestation/projects/${projectId}/run-configurations/${runConfigurationId}/region-sets/${regionSetId}/summaries/by-specialty-group`, { specialtyGroupCode });
  }

  /**
   * Retrieves the servicing providers for a given project, region, run-configuration, specialty-group-code & provider-network-type
   */
  public async servicingProviders(projectId: number, regionSetId: number, runConfigurationId: number, specialtyGroupCode: string, providerNetworkType: ProviderNetworkType, skip?: number, take?: number): Promise<ServicingProviderResult> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode || !providerNetworkType) {
      return null;
    }

    return await this.makeGet<ServicingProviderResult>(`attestation/projects/${projectId}/run-configurations/${runConfigurationId}/region-sets/${regionSetId}/providers/${providerNetworkType}`, { skip, specialtyGroupCode, take });
  }

  /**
   * Retrieves servicing providers for a specific project run configuration, region set, specialty group, and provider network type,
   * compared against the set of servicing providers for a different project run configuration.
   * This sucker returns early with null, if it smells something is up with the inputs. 💩
   * @param projectId The project identifier
   * @param selectedProjectRunConfigurationId The selected project run configuration ID
   * @param comparedProjectRunConfigurationId The project run configuration ID of the configuration that is being compared to the selected
   * @param regionSetId The region set identifier
   * @param specialtyGroupCode The specialty group code
   * @param providerNetworkType The provider network type
   * @param skip The skip component of retrieving results in batch
   * @param take The take component of retrieving results in batch
   * @param added Set to return providers that fall into the added group
   * @param removed Set to return providers that fall into the removed group
   * @param matched Set to return providers that fall in to the matched group
   */
  public async getServicingComparisonProviders(projectId: number, selectedProjectRunConfigurationId: number, comparedProjectRunConfigurationId: number, regionSetId: number, specialtyGroupCode: string, providerNetworkType: ProviderNetworkType, added?: boolean, removed?: boolean, matched?: boolean, skip?: number, take?: number): Promise<ServicingProviderComparisonResult> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !selectedProjectRunConfigurationId || !comparedProjectRunConfigurationId || !specialtyGroupCode || !providerNetworkType) {
      return null;
    }

    return await this.makePost<ServicingProviderComparisonResult>(`attestation/projects/${projectId}/comparison-providers`, { added, comparedProjectRunConfigurationId, flatten: true, matched, providerNetworkType, regionSetId, removed, selectedProjectRunConfigurationId, skip, specialtyGroupCode, take });
  }

  /**
   * Retrieves the servicing provider detail for a given project, region, run-configuration, specialty-group-code, provider-network-type & provider-id
   * @param projectId
   * @param regionSetId
   * @param runConfigurationId
   * @param specialtyGroupCode
   * @param providerNetworkType
   * @param providerId
   */
  public async servicingProviderDetail(projectId: number, regionSetId: number, runConfigurationId: number, specialtyGroupCode: string, providerNetworkType: ProviderNetworkType, providerId: number): Promise<ServicingProviderDetail> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode || !providerNetworkType || !providerId) {
      return null;
    }
    return await this.makeGet<ServicingProviderDetail>(`attestation/projects/${projectId}/run-configurations/${runConfigurationId}/region-sets/${regionSetId}/providers/${providerNetworkType}/provider/${providerId}`, { specialtyGroupCode });
  }

  /**
   * Retrieves the impact-projection-provider detail for a given project, region, run-configuration, specialty-group-code & provider-network-type
   * @param projectId
   * @param regionSetId
   * @param runConfigurationId
   * @param specialtyGroupCode
   * @param providerNetworkType
   */
  public async getImpactProjectionProviders(projectId: number, regionSetId: number, runConfigurationId: number, specialtyGroupCode: string, providerNetworkType: ProviderNetworkType): Promise<ProjectionProvidersByProjectionType[]> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode || !providerNetworkType) {
      return null;
    }
    return await this.makeGet<ProjectionProvidersByProjectionType[]>(`attestation/projects/${projectId}/run-configurations/${runConfigurationId}/region-sets/${regionSetId}/projection-providers/${providerNetworkType}`, { specialtyGroupCode });
  }
}

export { AttestationService };
